import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Smartphone, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardBody, CardTitle, Badge } from '@/shared/ui';
import { usePhoneboardStatusSummaries } from '@/entities/phoneboard';
import type { Phoneboard } from '@/shared/lib/types';

interface PhoneboardCardProps {
  phoneboard: Phoneboard;
}

const PhoneboardCard: React.FC<PhoneboardCardProps> = ({ phoneboard }) => {
  const statusConfig = {
    normal: { label: '정상', variant: 'success' as const },
    partial_error: { label: '일부비상', variant: 'warning' as const },
    error: { label: '비상', variant: 'danger' as const },
  };

  const status = statusConfig[phoneboard.status];
  const connectedPhones = phoneboard.phones?.filter(p => p.status === 'connected').length || phoneboard.connectedPhones;
  const totalPhones = phoneboard.phones?.length || phoneboard.totalPhones;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 hover:border-primary hover:bg-gray-50/30 transition-all duration-300 cursor-pointer group">
      <div className="flex justify-between items-center mb-3">
        <span className="font-medium">{phoneboard.name}</span>
        <Badge variant={status.variant}>
          {status.label}
        </Badge>
      </div>
      <div className="flex flex-wrap gap-1">
        {Array.from({ length: totalPhones }, (_, i) => (
          <div
            key={i}
            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs group-hover:scale-110 transition-transform ${
              i < connectedPhones
                ? 'bg-success/20 text-success'
                : 'bg-gray-200 text-gray-500'
            }`}
          >
            <Smartphone className="w-3 h-3" />
          </div>
        ))}
      </div>
      <div className="text-xs text-gray-500 mt-2">
        {connectedPhones}/{totalPhones} 연결
      </div>
    </div>
  );
};

export const PhoneboardList: React.FC = () => {
  // Fetch first 5 phoneboards for dashboard display
  const { data: statusSummariesResponse, isLoading } = usePhoneboardStatusSummaries({
    page: 0,
    size: 5,
  });

  // Transform API response to Phoneboard type
  const displayPhoneboards = useMemo(() => {
    const phoneboardSummaries = statusSummariesResponse?.data?.content || [];

    return phoneboardSummaries.map((summary): Phoneboard => {
      // Map API health_status to local status type
      let status: 'normal' | 'partial_error' | 'error';
      switch (summary.health_status) {
        case 'NORMAL':
          status = 'normal';
          break;
        case 'PARTIAL':
          status = 'partial_error';
          break;
        case 'CRITICAL':
          status = 'error';
          break;
        default:
          status = 'normal';
      }

      return {
        id: String(summary.id),
        name: summary.phoneboard_name || 'Unknown',
        status,
        connectedPhones: summary.connection_status.connected_count,
        totalPhones: summary.connection_status.total_count,
        phones: [],
        lastUpdated: summary.last_updated,
      };
    });
  }, [statusSummariesResponse]);

  return (
    <Card className="bg-gray-50/30">
      <CardHeader className="bg-white border-b border-gray-100">
        <CardTitle>폰보드 관리</CardTitle>
      </CardHeader>

      <CardBody>
        <div className="mb-6">
          {isLoading ? (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="border border-gray-200 rounded-lg p-3 animate-pulse">
                  <div className="flex justify-between items-center mb-3">
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                    <div className="h-5 bg-gray-200 rounded w-12"></div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {[...Array(5)].map((_, j) => (
                      <div key={j} className="w-6 h-6 bg-gray-200 rounded-full"></div>
                    ))}
                  </div>
                  <div className="h-3 bg-gray-200 rounded w-16 mt-2"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {displayPhoneboards.map((phoneboard) => (
                <PhoneboardCard key={phoneboard.id} phoneboard={phoneboard} />
              ))}
            </div>
          )}
          
          <Link 
            to="/phoneboards"
            className="mt-4 text-primary text-sm font-medium hover:underline inline-flex items-center"
          >
            전체보기 <ChevronRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
      </CardBody>
    </Card>
  );
};